import type { SpeciesSummary } from '@api/types'
import { useRegions } from '@features/places/usePlaces'
import { useI18n } from '@i18n/I18nProvider'
import { Icon } from '@ui/Icon'
import { Photo } from '@ui/Photo'
import { Screen } from '@ui/Screen'
import { ErrorState, LoadingState } from '@ui/States'
import { useDeferredValue, useMemo, useState } from 'react'
import { Link } from 'wouter'
import { regionName } from './SpeciesDetails'
import { searchSpecies } from './species-search'
import { speciesHref, useSpeciesList } from './useSpecies'

// "Formación" del mockup ("Aula de Naturaleza"). En la v1 lleva las fichas de especie; las
// audioguías y los vídeos del mockup quedan fuera de la v1 (P20) y se anuncian como próximos.
export const LearnPage = () => {
	const { data, error, isPending, refetch } = useSpeciesList()

	if (isPending)
		return (
			<Screen>
				<LoadingState shape="list" />
			</Screen>
		)
	if (error || !data) return <ErrorState error={error} onRetry={() => refetch()} />

	return <LearnContent items={data.items} />
}

const LearnContent = ({ items }: { items: SpeciesSummary[] }) => {
	const { t } = useI18n()
	const regions = useRegions()
	const [query, setQuery] = useState('')
	// La lista se filtra con el valor diferido: escribir nunca espera a que se repinte la lista.
	const deferredQuery = useDeferredValue(query)
	const species = useMemo(() => [...items].sort((a, b) => a.commonName.localeCompare(b.commonName)), [items])
	const found = useMemo(
		() => searchSpecies(species, deferredQuery, (id) => regionName(regions.data?.items, id)),
		[species, deferredQuery, regions.data]
	)
	const searching = deferredQuery.trim() !== ''
	return (
		<Screen title={t('learn.title')}>
			<p className="mb-6 text-lg">{t('learn.intro')}</p>

			<section aria-labelledby="learn-species">
				<h2 id="learn-species" className="mb-3 flex items-center gap-2 text-2xl font-semibold">
					<Icon name="leaf" className="size-6 text-forest-700" />
					{t('learn.species')}
				</h2>
				{species.length === 0 ? (
					<p className="py-4 text-earth-800">{t('learn.speciesEmpty')}</p>
				) : (
					<>
						<SpeciesSearch query={query} onChange={setQuery} />
						<p aria-live="polite" className="mb-3 text-earth-800">
							{searching &&
								found.length > 0 &&
								t('learn.searchCount', { n: found.length, total: species.length })}
						</p>
						{found.length === 0 ? (
							<p className="py-4 text-lg text-earth-800">
								{t('learn.searchEmpty', { query: deferredQuery.trim() })}
							</p>
						) : (
							<ul className="grid gap-3">
								{found.map((item) => (
									<SpeciesCard key={item.id} species={item} />
								))}
							</ul>
						)}
					</>
				)}
			</section>

			<section aria-labelledby="learn-media" className="mt-8 rounded-3xl bg-sage-200 p-5">
				<h2 id="learn-media" className="flex items-center gap-2 text-2xl font-semibold">
					<Icon name="headphones" className="size-6 text-olive-900" />
					{t('learn.mediaTitle')}
				</h2>
				<p className="mt-1 text-sm font-bold tracking-wide text-olive-900 uppercase">{t('learn.mediaSoon')}</p>
				<p className="mt-2">{t('learn.mediaText')}</p>
			</section>
		</Screen>
	)
}

// Una sola caja para buscar por nombre común, nombre científico o comunidad (species-search.ts).
const SpeciesSearch = ({ query, onChange }: { query: string; onChange: (query: string) => void }) => {
	const { t } = useI18n()
	return (
		<div role="search" className="relative mb-2">
			<label htmlFor="species-search" className="sr-only">
				{t('learn.search')}
			</label>
			<Icon
				name="search"
				className="pointer-events-none absolute top-1/2 left-4 size-6 -translate-y-1/2 text-earth-600"
			/>
			<input
				id="species-search"
				type="search"
				value={query}
				autoComplete="off"
				autoCorrect="off"
				spellCheck={false}
				enterKeyHint="search"
				placeholder={t('learn.searchPlaceholder')}
				onChange={(e) => onChange(e.target.value)}
				onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
				className="min-h-14 w-full rounded-2xl border-2 border-earth-300 bg-white pr-14 pl-12 text-lg focus:border-forest-800 focus:outline-none [&::-webkit-search-cancel-button]:hidden"
			/>
			{query !== '' && (
				<button
					type="button"
					aria-label={t('learn.searchClear')}
					onClick={() => {
						onChange('')
						document.getElementById('species-search')?.focus()
					}}
					className="absolute top-1/2 right-1 flex size-12 -translate-y-1/2 items-center justify-center rounded-full text-earth-800 active:bg-earth-100"
				>
					<Icon name="close" className="size-6" />
				</button>
			)}
		</div>
	)
}

const SpeciesCard = ({ species }: { species: SpeciesSummary }) => (
	<li>
		<Link
			href={speciesHref(species.id)}
			className="flex items-center gap-3 rounded-3xl bg-white p-3 shadow-sm active:bg-earth-100"
		>
			{species.photoUrl ? (
				<Photo src={species.photoUrl} alt="" className="size-20 shrink-0 rounded-2xl object-cover" />
			) : (
				<span className="flex size-20 shrink-0 items-center justify-center rounded-2xl bg-sage-50 text-forest-700">
					<Icon name="pine" className="size-8" />
				</span>
			)}
			<div className="min-w-0 flex-1">
				<h3 className="text-lg font-bold text-forest-800">{species.commonName}</h3>
				<p className="text-earth-800 italic">{species.scientificName}</p>
			</div>
			<Icon name="forward" className="size-6 shrink-0 text-earth-600" />
		</Link>
	</li>
)
